import { all, call, put, takeLatest } from 'redux-saga/effects'
import selectIdToken from '../selectors/select-id-token'
import {
  CreateNewNoticeAction,
  CreateNewNoticeFailureAction,
  CreateNewNoticeSuccessAction,
  DeleteNoticeAction,
  DeleteNoticeFailureAction,
  DeleteNoticeSuccessAction,
  FetchNoticesAction,
  FetchNoticesFailureAction,
  FetchNoticesSuccessAction,
  MarkNoticeAsReadAction,
  MarkNoticeAsReadSuccessAction
} from './notices-actions'
import { selectFromState } from '../selectors/select-from-state'
import { type GetNoticesResponse, type MarkNoticeAsReadResponse, NoticesApi } from '../../api/notices-api'
import { AddMessageAction } from '../messages/messages-actions'
import { ApiUtil } from '../../api/api-utils'
import { MessageTypes } from '../messages/messages-types'
import { AdminNoticesApi, type CreateNoticeResponse, type DeleteNoticeResponse } from '../../api/admin-notices-api'

/* Sage Generator Functions */

// Note: disabling requiring explicit return types for saga generators
// (as the type is pretty complex and produces little value as explicit)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function * fetchNotices() {
  // note: 'select' function do not work well with TypeScript types
  // (i.e., the return value does not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const res: GetNoticesResponse = yield call(NoticesApi.getNotices, idToken)
  if (res.succeeded) {
    yield put(FetchNoticesSuccessAction(res.payload.notices))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(res)
    console.warn('Error received on fetching notices:', errMsg)
    // yield put(AddMessageAction(error.message, MessageTypes.error))
    yield put(FetchNoticesFailureAction())
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function * createNewNotice(action: { payload: string }) {
  // note: 'select' function do not work well with TypeScript types
  // (i.e., the return value does not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const res: CreateNoticeResponse = yield call(AdminNoticesApi.createNotice, action.payload, idToken)

  if (res.succeeded) {
    yield put(CreateNewNoticeSuccessAction(res.payload.notice))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(res)
    console.warn('Error received on creating new notice:', errMsg)

    yield all([
      put(CreateNewNoticeFailureAction()),
      put(AddMessageAction('notices.createFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function * deleteNotice(action: { payload: string }) {
  const noticeId = action.payload

  // note: 'select' function do not work well with TypeScript types
  // (i.e., the return value does not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const res: DeleteNoticeResponse = yield call(AdminNoticesApi.deleteNotice, noticeId, idToken)

  if (res.succeeded) {
    yield put(DeleteNoticeSuccessAction(noticeId))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(res)
    console.warn('Error received on deleting notice:', errMsg)
    // yield put(AddMessageAction(error.message, MessageTypes.error))
    yield all([
      put(DeleteNoticeFailureAction()),
      put(AddMessageAction('notices.deleteFailedDefaultError', MessageTypes.error))
    ])
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function * markNoticeAsRead(action: { payload: string }) {
  const noticeId = action.payload

  // note: 'select' function do not work well with TypeScript types
  // (i.e., the return value does not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const res: MarkNoticeAsReadResponse = yield call(NoticesApi.markAsRead, noticeId, idToken)
  if (res.succeeded) {
    yield put(MarkNoticeAsReadSuccessAction(res.payload.notice))
  } else {
    const errMsg = ApiUtil.failedResponseToDisplayString(res)
    console.warn('Error received on marking notice as read:', errMsg)
    yield put(AddMessageAction('notices.markAsReadFailedDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function * noticesSagas() {
  yield takeLatest(FetchNoticesAction, fetchNotices)
  yield takeLatest(DeleteNoticeAction, deleteNotice)
  yield takeLatest(CreateNewNoticeAction, createNewNotice)
  yield takeLatest(MarkNoticeAsReadAction, markNoticeAsRead)
}
